<!-- Copyright: Seán I. O'Donoghue -->

<!-- eslint-disable vue/no-v-html -->
<template>
  <DialogModal v-if="currentPerson" :fixed-width="false" @hide="hitItAndQuit">
    <figure v-if="people && people[currentPerson]" @click.stop>
      <!-- the headshot opens a talk video (headshotVideo), else the speaker's web page -->
      <component :is="headshotVideo ? 'router-link' : 'a'" v-bind="headshotLink" class="headshot">
        <img v-if="headshot(currentPerson)" :src="headshot(currentPerson)" />
        <img
          v-else-if="smallHeadshot(currentPerson)"
          class="small"
          :src="smallHeadshot(currentPerson)" />
      </component>
      <figcaption>
        <!-- person's name -->
        <div>
          <h4>
            <a :href="people[currentPerson].URL">
              <!-- https://uxwing.com/hyperlink-icon/ -->
              {{ currentPerson }}
              <img src="@/assets/img/hyperlink.svg" />
            </a>
            <!-- the speaker's videos (/Videos/David_Goodsell: the list, or the one video) -->
            <router-link
              v-if="speakerVideos.length"
              :to="`/Videos/${underscore(currentPerson)}`"
              class="videoLink"
            >
              <VideoPlayIcon :label="videoLinkLabel" />
            </router-link>
          </h4>
          <!-- affiliation -->
          <p class="affiliation">{{ people[currentPerson].Affiliation }}</p>
        </div>
        <SwitchBespoke
          v-if="bio && description"
          :show-bio="!showBio"
          style="float: right"
          @update="showBio = !showBio" />
        <br />
        <!-- the talk description: when switched to, or when there's no bio to switch from -->
        <p v-if="description && (showBio || !bio)" class="bio">
          <Slides :name="currentPerson" :year="sheetYear" align="left" />
          <span v-html="description" />
        </p>
        <p v-else-if="bio" class="bio">
          <Slides :name="currentPerson" :year="sheetYear" align="left" />
          <span v-html="bio" />
        </p>
        <p v-else-if="topic" class="topic">
          <Slides :name="currentPerson" :year="sheetYear" align="left" />
          <span v-html="topic" />
        </p>
      </figcaption>
    </figure>
  </DialogModal>
</template>

<script>
//import ButtonBespoke from '@/components/ButtonBespoke';
import DialogModal from "@/components/DialogModal";
import stringify from "json-stringify-safe";
import SwitchBespoke from "@/components/SwitchBespoke";
import Slides from "@/components/People/Person/Slides";
import VideoPlayIcon from "@/components/Program/VideoPlayIcon";
import useSpeakerIndex from "@/composables/useSpeakerIndex";
export default {
  name: "PersonHighlight",
  //emits: ['closeHighlight'], // https://stackoverflow.com/q/64220737
  components: {DialogModal, SwitchBespoke, Slides, VideoPlayIcon}, //Button
  props: {
    category: {type: String, default: ""},
    day: {type: String, default: ""},
    people: {type: Object, default: () => {}},
    person: {type: String, required: true},
    selectedPeople: {type: Array, default: () => []},
    year: {type: [String, Number], default: ""}, // else the route's (/2024/People)
    keepsAddress: {type: Boolean, default: false} // the caller keeps the address (useSpeakerDialog)
  },
  emits: ["hide"],
  setup() {
    const {getSpeaker} = useSpeakerIndex();
    return {getSpeaker};
  },
  data() {
    return {
      currentPerson: "",
      hashBaseParams: "", // hash parameters aside from person's name
      personInInitialUrl: "",
      showBio: false
    };
  },
  computed: {
    sheetYear() {
      return this.year || this.$route.params.year;
    },
    speakerVideos() {
      return this.getSpeaker(this.currentPerson)?.videos || [];
    },
    // with a year in the URL (/2024/People), that year's talk video, else none (the web page);
    // elsewhere (e.g. /Videos/Unlock), the latest
    headshotVideo() {
      const videos = this.speakerVideos;
      const urlYear = this.$route.params.year;
      if (!urlYear) return videos[0];
      return videos.find((video) => String(video.year) === String(urlYear));
    },
    videoLinkLabel() {
      const count = this.speakerVideos.length;
      return count === 1 ? "Watch the video" : `Watch ${count} videos`;
    },
    headshotLink() {
      if (this.headshotVideo) return {to: `/Videos/${this.headshotVideo.hash}`};
      return {href: this.people[this.currentPerson]?.URL};
    },
    description() {
      this.log(`category = ${this.category}`);
      if (
        this.people &&
        this.people[this.currentPerson] &&
        this.people[this.currentPerson]["Description"]
      ) {
        this.log("if there is at least one talk description, ...");
        let description = this.people[this.currentPerson]["Description"];
        this.log(`description = ${stringify(description)}`);
        if (this.category && description[this.category]) {
          this.log("return description for currently selected session");
          return description[this.category];
        } else {
          this.log("no session is selected, so return first description");
          // https://stackoverflow.com/q/3298477
          return Object.values(description)[0];
        }
      } else {
        // if no description, return false
        return false;
      }
    },
    bio() {
      if (
        this.people &&
        this.people[this.currentPerson] &&
        this.people[this.currentPerson]["Bio"]
      ) {
        return this.people[this.currentPerson]["Bio"];
      } else {
        return false;
      }
    },
    topic() {
      if (!this.people) return false;
      if (!this.people[this.currentPerson]) return false;
      console.log(JSON.stringify(this.people[this.currentPerson]));
      if (!this.people[this.currentPerson]["Topic"]) return false;
      return this.people[this.currentPerson]["Topic"];
    }
  },
  watch: {
    // its links (headshot, videos) go to another page: close, so the overlay doesn't stay (and
    // the page scrolls again: DialogModal)
    "$route.path"() {
      if (!this.currentPerson) return;
      this.currentPerson = "";
      this.$emit("hide");
    },
    // a person's name arriving in the address once the page is up (a link within the site, e.g.
    // the 2026 sidebar's Highlights; the page kept alive, so not mounted again): their dialog
    // opens, as on a reload (Seán, 2026-10-22). Their own address changes (setPersonInUrl) find
    // them already shown
    "$route.hash"(hash) {
      if (this.person) return;
      const last = decodeURIComponent(hash || "").replace(/#/, "").split(/&/).at(-1) || "";
      if (!last.includes("_")) return;
      const name = last.replaceAll("_", " ");
      if (name === this.currentPerson || !this.selectedPeople.includes(name)) return;
      this.currentPerson = name;
    },
    selectedPeople: function () {
      // newVal, oldVal
      this.log("selectedPeople changed");
      if (this.person) {
        this.currentPerson = this.person;
        return; // early exit
      }
      if (this.personInInitialUrl) {
        if (this.selectedPeople.includes(this.personInInitialUrl)) {
          this.currentPerson = this.personInInitialUrl;
          this.setPersonInUrl(this.currentPerson);
          this.log(`'${this.personInInitialUrl}' is in ${stringify(this.selectedPeople)}`);
        } else {
          this.log(`'${this.personInInitialUrl}' is not in ${stringify(this.selectedPeople)}`);
        }
        this.personInInitialUrl = ""; // forgot person in initial URL
      }
    }
  },
  mounted() {
    this.log(`personHightlight() mounted`);
    if (this.person) {
      this.log(`personHightly mounted for ${this.person}`);
      if (this.personInInitialUrl) {
        this.personInInitialUrl = ""; // forgot person in initial URL
      }
      this.currentPerson = this.person;
      this.setPersonInUrl(this.currentPerson);
      return; //early exit
    }
    if (window.location.hash) {
      // this.$route.hash doesn't work
      // decodeURIComponent
      let hash = decodeURIComponent(window.location.hash);
      this.log(`URL contains hash: '${hash}'`);
      let params = hash.replace(/#/, "").split(/&/);
      if (params && params.length > 0) {
        this.log(`URL contains parameters: '${stringify(params)}'`);
        // https://stackoverflow.com/q/60548305
        let lastParam = params.at(-1);
        if (lastParam.match(/_/)) {
          this.log(`URL contains a person's name: ${lastParam}`);
          this.personInInitialUrl = lastParam.replaceAll(/_/g, " ");
          params.shift(); // remove last parameter from list
          this.setPersonInUrl(""); // clear url to prevent progegation
        }
        if (params.length > 0) {
          this.log(`URL contains other parameters: ${stringify(params)}`);
          this.hashBaseParams = params.join("&");
          this.log(`this.hashBaseParams = ${this.hashBaseParams}`);
        }
      }
    }
  },
  methods: {
    // eslint-disable-next-line
    log(message) {
      if (process.env.NODE_ENV === "development") {
        //console.log(message); // uncomment to show logs from this component
      }
    },
    goTo(person) {
      this.currentPerson = person;
      this.setPersonInUrl(person);
    },
    underscore(person) {
      return person.replaceAll(" ", "_");
    },
    headshot(person) {
      try {
        return require(`@/assets/years/${
          this.sheetYear
        }/People/img/large/${this.underscore(person)}.jpg`);
      } catch (error) {
        this.log(`No large headshot found for ${person}`);
        return false;
      }
    },
    smallHeadshot(person) {
      try {
        return require(`@/assets/years/${this.sheetYear}/People/img/${person.replaceAll(
          " ",
          "_"
        )}.png`);
      } catch (error) {
        this.log(`Failed to find image for ${person}`);
        return require(`@/assets/years/${this.sheetYear}/People/img/large/Unknown.jpg`);
      }
    },
    setPersonInUrl(currentPerson) {
      if (this.keepsAddress) return;
      this.log(`setPersonInUrl(${currentPerson})`);
      if (currentPerson) {
        currentPerson = this.underscore(currentPerson);
      }
      let newHash = "";
      if (this.hashBaseParams && currentPerson) {
        newHash = this.hashBaseParams + "&" + currentPerson;
      } else if (currentPerson) {
        // if no hashBaseParams
        newHash = currentPerson;
      }
      this.log(`about to set hash: ${newHash}`);
      this.addHashToLocation(newHash);
    },
    addHashToLocation(newHash) {
      // silently-update-url-without-triggering-route-in-vue-router
      // https://stackoverflow.com/q/51337255/
      this.log(`addHashToLocation(${newHash})`);
      if (newHash) {
        history.pushState({}, null, `${this.$route.path}#${newHash}`);
      } else {
        // empty hash
        history.pushState({}, null, this.$route.path);
      }
    },
    hitItAndQuit() {
      this.log(`personHighlight/Let's hitItAndQuit()`);
      this.log(`personHighlight/hitItAndQuit()`);
      this.currentPerson = ""; // disables highlight
      this.setPersonInUrl(); // clear URL
      this.$emit("hide");
    }
  }
};
</script>

<style scoped>
figure {
  margin: auto;
  max-width: 95%;
  overflow: hidden; /* Add this line */
  /* clear: both; */
}
.headshot img {
  position: relative;
  width: 100%;
  max-width: 600px;
  display: block;
  margin: auto;
}
body:not(.narrow) .headshot img.small {
  width: calc(167px * 1.3); /* if only small img available, scale it up a bit */
  height: auto; /* This maintains the aspect ratio */
  object-fit: contain; /* Fit within the parent container */
}
figcaption {
  margin: 20px 0 10px 0;
}
figcaption div {
  float: left;
  text-align: left;
}
h4 {
  /*  offset the hyperlink SVG */
  color: black;
  font-weight: 600;
  font-size: clamp(16px, 2.5vw, 1.3em);
  line-height: 1em;
  margin: 0px;
  /* top, right, bottom, and left */
}
h4 a {
  color: inherit; /* the name stays black, as when the link wrapped the heading */
}
/* the talk videos, after the web-page link icon, in a row */
.videoLink {
  margin-left: 0.35em;
  color: var(--brand-color, #33cc99);
}
.videoLink:hover {
  filter: brightness(0.85);
}
svg {
  margin-right: 8px;
}
p.affiliation {
  margin: 0px;
  font-size: clamp(0.8em, 2vw, 1.1em);
}
br {
  clear: both;
}
p {
  color: black;
}
p.bio {
  max-width: 600px;
  font-size: 0.9em;
  line-height: 1.3em;
  margin: 8px 0 8px 0;
  /* top, right, bottom, and left */
  hyphens: auto;
  text-align: justify;
}
p.topic {
  max-width: 600px;
  font-size: 0.9em;
  line-height: 1.3em;
  margin: 8px 0 8px 0;
  /* top, right, bottom, and left */
  hyphens: auto;
  text-align: left;
}
</style>
